import { user } from '@/features/users/types'
import { configureUserTimeZone } from '@/utils/timeZone'
import { useTimeEntriesStore } from './useTimeEntriesStore'
import { getFilterDateRange } from '@/features/timeEntries/utils/dateRange'
import moment from 'moment'
import { create } from 'zustand'

type Updater<T> = T | ((previous: T) => T)

const resolveValue = <T>(value: Updater<T>, previous: T): T =>
  typeof value === 'function' ? (value as (previous: T) => T)(previous) : value

type UserState = {
  username: string
  delegations: user[]
  selfUser: user | null
  selectedUser: user | null
  isDelegationMode: boolean
  emailExclusionList: any[]
  setUsername: (username: string) => void
  setDelegations: (delegations: user[]) => void
  setSelfUser: (selfUser: user) => void
  setSelectedUser: (selectedUser: user) => void
  setIsDelegationMode: (isDelegationMode: boolean) => void
  setEmailExclusionList: (emailExclusionList: Updater<any[]>) => void
}

const getInitialUsername = () => {
  if (typeof localStorage === 'undefined') return ''
  return localStorage.getItem('userDisplayName') || ''
}

export const useUserStore = create<UserState>((set, get) => ({
  username: getInitialUsername(),
  delegations: [],
  selfUser: null,
  selectedUser: null,
  isDelegationMode: false,
  emailExclusionList: [],
  setUsername: username => set({ username }),
  setDelegations: delegations => set({ delegations }),
  setSelfUser: selfUser => set({ selfUser }),
  setSelectedUser: selectedUser => {
    const previousUser = get().selectedUser
    const { filter, step, selectedWeekDay } = useTimeEntriesStore.getState()
    // MIRA-4355: Capture the calendar date before changing Moment's timezone.
    // LA Saturday can already be Sunday in Taipei, so reusing the old week
    // offset would move the selected Saturday forward by seven days.
    const dateKey = previousUser
      ? filter === 3 && selectedWeekDay
        ? selectedWeekDay.mappingDate ||
          moment(selectedWeekDay.date).format('YYYY-MM-DD')
        : getFilterDateRange(filter, step).startDate
      : null
    configureUserTimeZone(selectedUser)
    if (dateKey) {
      // Rebase the offset and selected date in the new timezone so both the
      // displayed date and the query range stay on the same calendar period.
      const date = moment(dateKey, 'YYYY-MM-DD')
      const unit = filter === 1 ? 'month' : 'week'
      const nextStep = date
        .clone()
        .startOf(unit)
        .diff(moment().startOf(unit), unit)
      useTimeEntriesStore.setState({
        step: nextStep,
        ...(filter === 3 && selectedWeekDay
          ? { selectedWeekDay: { ...selectedWeekDay, date } }
          : {}),
      })
    }
    set({ selectedUser })
  },
  setIsDelegationMode: isDelegationMode => set({ isDelegationMode }),
  setEmailExclusionList: emailExclusionList =>
    set(state => ({
      emailExclusionList: resolveValue(
        emailExclusionList,
        state.emailExclusionList
      ),
    })),
}))
